/**
 * Audio effects (spec section 19).
 *
 * Every effect is a small Web Audio graph with a wet/dry balance driven
 * by "Effect Strength" (0-100%). Speech intelligibility has priority, so
 * the wet gain is capped well below unity and nothing here changes pitch
 * or timing.
 *
 * Graphs are built from plain nodes - no AudioWorklet - so they work on
 * every browser that supports Web Audio, including mobile Chrome.
 */

import { AUDIO_EFFECTS } from '../core/config.js';

export const EFFECT_IDS = AUDIO_EFFECTS.map((e) => e.id);

/** Cap on how much wet signal any effect may add. */
const MAX_WET = { headphones: 0.55, car_radio: 0.7, telephone: 0.85, room: 0.4, tape: 0.6 };

export function getEffectDefinition(id) {
  return AUDIO_EFFECTS.find((e) => e.id === id) ?? AUDIO_EFFECTS[0];
}

/** Soft-clip curve for saturation stages. */
function makeSaturationCurve(amount = 2.2) {
  const samples = 1024;
  const curve = new Float32Array(samples);
  for (let i = 0; i < samples; i += 1) {
    const x = (i * 2) / (samples - 1) - 1;
    curve[i] = Math.tanh(x * amount) / Math.tanh(amount);
  }
  return curve;
}

/** Exponentially decaying noise - a usable room impulse without assets. */
function createImpulseResponse(ctx, seconds = 0.9, decay = 2.6) {
  const rate = ctx.sampleRate;
  const length = Math.max(1, Math.floor(rate * seconds));
  const impulse = ctx.createBuffer(2, length, rate);
  for (let channel = 0; channel < 2; channel += 1) {
    const data = impulse.getChannelData(channel);
    for (let i = 0; i < length; i += 1) {
      const envelope = Math.pow(1 - i / length, decay);
      data[i] = (Math.random() * 2 - 1) * envelope;
    }
  }
  return impulse;
}

function gain(ctx, value) {
  const node = ctx.createGain();
  node.gain.value = value;
  return node;
}

function biquad(ctx, type, frequency, q = 0.707, gainDb = 0) {
  const node = ctx.createBiquadFilter();
  node.type = type;
  node.frequency.value = frequency;
  if (type === 'peaking' || type === 'lowshelf' || type === 'highshelf') node.gain.value = gainDb;
  if (type === 'highpass' || type === 'lowpass' || type === 'peaking') node.Q.value = q;
  return node;
}

/**
 * Build the effect graph.
 *
 * @returns {{input: AudioNode, output: AudioNode, nodes: AudioNode[], dispose: Function, wetAmount: number}}
 */
export function buildEffectGraph(ctx, effectId = 'none', { strength = 0.35 } = {}) {
  const amount = Math.min(1, Math.max(0, Number(strength) || 0));
  const input = gain(ctx, 1);
  const output = gain(ctx, 1);
  const nodes = [input, output];
  const disposers = [];

  if (effectId === 'none' || !EFFECT_IDS.includes(effectId)) {
    input.connect(output);
    return { input, output, nodes, dispose: () => {}, wetAmount: 0, id: 'none' };
  }

  const wet = gain(ctx, MAX_WET[effectId] * amount);
  const dry = gain(ctx, 1);
  nodes.push(wet, dry);
  input.connect(dry);
  dry.connect(output);

  switch (effectId) {
    case 'headphones': {
      // Close, intimate, slightly narrowed stereo with a gentle crossfeed.
      const lowCut = biquad(ctx, 'highshelf', 5200, 0.7, 2.5 * amount);
      const warmth = biquad(ctx, 'lowshelf', 180, 0.7, -1.5 * amount);
      const splitter = ctx.createChannelSplitter(2);
      const merger = ctx.createChannelMerger(2);
      const crossGain = gain(ctx, 0.22 * amount);
      const delayL = ctx.createDelay(0.02);
      const delayR = ctx.createDelay(0.02);
      delayL.delayTime.value = 0.0003;
      delayR.delayTime.value = 0.00035;
      nodes.push(lowCut, warmth, splitter, merger, crossGain, delayL, delayR);

      input.connect(lowCut);
      lowCut.connect(warmth);
      warmth.connect(splitter);
      splitter.connect(merger, 0, 0);
      splitter.connect(merger, 1, 1);
      splitter.connect(delayR, 0);
      delayR.connect(crossGain);
      crossGain.connect(merger, 0, 1);
      splitter.connect(delayL, 1);
      const crossGain2 = gain(ctx, 0.22 * amount);
      nodes.push(crossGain2);
      delayL.connect(crossGain2);
      crossGain2.connect(merger, 0, 0);
      merger.connect(wet);
      break;
    }

    case 'car_radio': {
      // Band-limited, mid-forward, with a hint of low rumble.
      const hp = biquad(ctx, 'highpass', 170, 0.8);
      const lp = biquad(ctx, 'lowpass', 5400, 0.8);
      const presence = biquad(ctx, 'peaking', 1900, 0.9, 4 * amount);
      const shaper = ctx.createWaveShaper();
      shaper.curve = makeSaturationCurve(1.6 + amount);
      shaper.oversample = '2x';
      const trim = gain(ctx, 0.85);
      const rumble = biquad(ctx, 'lowpass', 85, 0.9);
      const rumbleGain = gain(ctx, 0.18 * amount);
      nodes.push(hp, lp, presence, shaper, trim, rumble, rumbleGain);

      input.connect(hp);
      hp.connect(lp);
      lp.connect(presence);
      presence.connect(shaper);
      shaper.connect(trim);
      trim.connect(wet);
      input.connect(rumble);
      rumble.connect(rumbleGain);
      rumbleGain.connect(wet);
      break;
    }

    case 'telephone': {
      // 300-3400 Hz, collapsed to mono.
      const hp = biquad(ctx, 'highpass', 300, 0.9);
      const lp = biquad(ctx, 'lowpass', 3400, 0.9);
      const presence = biquad(ctx, 'peaking', 1500, 0.8, 5 * amount);
      const shaper = ctx.createWaveShaper();
      shaper.curve = makeSaturationCurve(1.4 + amount * 0.8);
      const splitter = ctx.createChannelSplitter(2);
      const monoSum = gain(ctx, 0.5);
      const mono = gain(ctx, 1);
      nodes.push(hp, lp, presence, shaper, splitter, monoSum, mono);

      input.connect(hp);
      hp.connect(lp);
      lp.connect(presence);
      presence.connect(shaper);
      shaper.connect(splitter);
      splitter.connect(monoSum, 0);
      splitter.connect(monoSum, 1);
      monoSum.connect(mono);
      mono.connect(wet);
      break;
    }

    case 'room': {
      // Short natural reverb from a generated impulse response.
      const preDelay = ctx.createDelay(0.2);
      preDelay.delayTime.value = 0.012;
      const convolver = ctx.createConvolver();
      convolver.normalize = true;
      convolver.buffer = createImpulseResponse(ctx, 0.5 + amount * 1.1, 2.2 + amount);
      const tone = biquad(ctx, 'lowpass', 6000, 0.7);
      const wetTrim = gain(ctx, 0.55);
      nodes.push(preDelay, convolver, tone, wetTrim);

      input.connect(preDelay);
      preDelay.connect(convolver);
      convolver.connect(tone);
      tone.connect(wetTrim);
      wetTrim.connect(wet);
      disposers.push(() => {
        convolver.buffer = null;
      });
      break;
    }

    case 'tape': {
      // Soft saturation, gentle high roll-off and a slow wow on a short delay.
      const shaper = ctx.createWaveShaper();
      shaper.curve = makeSaturationCurve(1.5 + amount);
      shaper.oversample = '2x';
      const rollOff = biquad(ctx, 'highshelf', 7000, 0.7, -3.5 * amount);
      const body = biquad(ctx, 'lowshelf', 120, 0.7, 1.2 * amount);
      const wowDelay = ctx.createDelay(0.05);
      wowDelay.delayTime.value = 0.012;
      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = 0.7;
      const lfoDepth = gain(ctx, 0.0009 * amount);
      nodes.push(shaper, rollOff, body, wowDelay, lfo, lfoDepth);

      input.connect(shaper);
      shaper.connect(body);
      body.connect(rollOff);
      rollOff.connect(wowDelay);
      wowDelay.connect(wet);
      lfo.connect(lfoDepth);
      lfoDepth.connect(wowDelay.delayTime);
      lfo.start();
      disposers.push(() => {
        try {
          lfo.stop();
        } catch {
          /* already stopped */
        }
      });
      break;
    }

    default:
      input.connect(output);
      return { input, output, nodes, dispose: () => {}, wetAmount: 0, id: effectId };
  }

  wet.connect(output);

  return {
    id: effectId,
    input,
    output,
    nodes,
    wetAmount: MAX_WET[effectId] * amount,
    dispose() {
      for (const fn of disposers) {
        try {
          fn();
        } catch {
          /* ignore */
        }
      }
      for (const node of nodes) {
        try {
          node.disconnect();
        } catch {
          /* already disconnected */
        }
      }
    },
  };
}

/** Plain-language summary shown next to the strength slider. */
export function describeEffect(effectId, strengthPercent = 35) {
  const def = getEffectDefinition(effectId);
  if (effectId === 'none' || strengthPercent <= 0) return 'Audio is passed through untouched.';
  return `${def.label} at ${Math.round(strengthPercent)}% - ${def.hint}`;
}
